import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Search, Trophy } from 'lucide-react';
import api from '../utils/api';

export default function ReferralsScreen() {
  const [tab, setTab] = useState('leaderboard'); // leaderboard | all | earnings
  const [leaderboard, setLeaderboard] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [earnings, setEarnings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (tab === 'leaderboard') {
        const data = await api.getLeaderboard();
        setLeaderboard(data.leaderboard || data.data || data || []);
      } else if (tab === 'all') {
        const data = await api.getReferrals(200);
        setReferrals(data.referrals || data.data || data || []);
      } else if (tab === 'earnings') {
        const data = await api.getReferralEarnings({ limit: 200 });
        setEarnings(data.earnings || data.data || data || []);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '—';
    return Number(num).toLocaleString('ru-RU');
  };

  const medalColors = ['text-yellow-400', 'text-slate-300', 'text-amber-600'];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-white">Рефералы</h2>
        <button
          onClick={loadData}
          disabled={loading}
          className="p-2 bg-slate-800 rounded-lg border border-slate-700 text-slate-400 hover:text-white disabled:opacity-50"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4">
        {[
          { id: 'leaderboard', label: 'Лидерборд' },
          { id: 'all', label: 'Все связи' },
          { id: 'earnings', label: 'Начисления' },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              tab === t.id
                ? 'bg-blue-600 text-white'
                : 'bg-slate-800 text-slate-400 hover:text-white border border-slate-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <input
          type="text"
          placeholder="Поиск..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-9 pr-4 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
        />
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-4 text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Leaderboard */}
      {tab === 'leaderboard' && (
        <div className="space-y-2">
          {loading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="bg-slate-800 rounded-xl border border-slate-700 p-4">
                <div className="h-5 bg-slate-700 rounded animate-pulse" />
              </div>
            ))
          ) : leaderboard.length === 0 ? (
            <div className="bg-slate-800 rounded-xl border border-slate-700 p-8 text-center text-slate-500">
              Нет данных
            </div>
          ) : (
            leaderboard
              .filter(item => {
                if (!searchQuery) return true;
                const q = searchQuery.toLowerCase();
                return String(item.userId || item.user_id || '').includes(q) ||
                  String(item.username || '').toLowerCase().includes(q);
              })
              .map((item, i) => (
                <div key={i} className="bg-slate-800 rounded-xl border border-slate-700 p-4 flex items-center gap-4">
                  <div className="w-8 text-center shrink-0">
                    {i < 3 ? (
                      <Trophy size={20} className={medalColors[i]} />
                    ) : (
                      <span className="text-slate-500 text-sm font-bold">{i + 1}</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white font-medium truncate">
                      {item.username ? `@${item.username}` : `ID: ${item.userId || item.user_id}`}
                    </p>
                    <p className="text-xs text-slate-500">
                      {item.firstName || item.first_name || ''}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-bold text-white">{formatNumber(item.referralsCount ?? item.count)} реф.</p>
                    {(item.totalEarnings ?? item.earnings) !== undefined && (
                      <p className="text-xs text-green-400">{formatNumber(item.totalEarnings ?? item.earnings)} ₽</p>
                    )}
                  </div>
                </div>
              ))
          )}
        </div>
      )}

      {/* All referrals */}
      {tab === 'all' && (
        <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-slate-400 text-xs border-b border-slate-700">
                  <th className="text-left p-3">Реферер</th>
                  <th className="text-left p-3">Реферал</th>
                  <th className="text-right p-3">Дата</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={3} className="p-8 text-center text-slate-500">Загрузка...</td></tr>
                ) : referrals.length === 0 ? (
                  <tr><td colSpan={3} className="p-8 text-center text-slate-500">Нет данных</td></tr>
                ) : (
                  referrals
                    .filter(r => {
                      if (!searchQuery) return true;
                      const q = searchQuery.toLowerCase();
                      return String(r.referrerId || r.referrer_id || '').includes(q) ||
                        String(r.referralId || r.referral_id || '').includes(q);
                    })
                    .map((ref, i) => (
                      <tr key={i} className="border-t border-slate-700/50 hover:bg-slate-700/30">
                        <td className="p-3 text-white text-xs">{ref.referrerId || ref.referrer_id}</td>
                        <td className="p-3 text-slate-300 text-xs">{ref.referralId || ref.referral_id}</td>
                        <td className="p-3 text-right text-slate-400 text-xs">{ref.createdAt || ref.created_at || '—'}</td>
                      </tr>
                    ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Earnings */}
      {tab === 'earnings' && (
        <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-slate-400 text-xs border-b border-slate-700">
                  <th className="text-left p-3">Реферер</th>
                  <th className="text-right p-3">Сумма</th>
                  <th className="text-center p-3">Статус</th>
                  <th className="text-right p-3">Дата</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={4} className="p-8 text-center text-slate-500">Загрузка...</td></tr>
                ) : earnings.length === 0 ? (
                  <tr><td colSpan={4} className="p-8 text-center text-slate-500">Нет данных</td></tr>
                ) : (
                  earnings
                    .filter(e => {
                      if (!searchQuery) return true;
                      return String(e.referrerId || e.referrer_id || '').includes(searchQuery);
                    })
                    .map((earning, i) => (
                      <tr key={i} className="border-t border-slate-700/50 hover:bg-slate-700/30">
                        <td className="p-3 text-white text-xs">{earning.referrerId || earning.referrer_id}</td>
                        <td className="p-3 text-right text-green-400">{formatNumber(earning.amount)} ₽</td>
                        <td className="p-3 text-center">
                          <span className={`px-2 py-0.5 rounded-full text-xs ${
                            earning.status === 'paid'
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-yellow-500/20 text-yellow-400'
                          }`}>
                            {earning.status === 'paid' ? 'Выплачено' : 'Ожидает'}
                          </span>
                        </td>
                        <td className="p-3 text-right text-slate-400 text-xs">{earning.createdAt || earning.created_at || '—'}</td>
                      </tr>
                    ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
